import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Colors from 'Wpds/colors/ColorConstants';
import * as goalsActions from 'Shared/actions/Goals.actions';
import * as GoalsSelectors from 'Shared/selectors/Goals.selectors';
import { useToggle } from 'Shared/utils/hooks';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Spinner from 'Shared/components/Spinner';
import Confetti from 'Shared/components/Confetti/Confetti';
import CreateOrUpdateGoalPageV2 from '../CreateOrUpdateGoalPage/CreateOrUpdateGoalPageV2';
import CreateOrUpdateGoalPageV3 from '../CreateOrUpdateGoalPageV3/CreateOrUpdateGoalPageV3';
import { getActionItems } from 'Shared/selectors/meetingItems';
import CompleteGoalModal from 'Shared/components/Goals/CompleteGoalModal/CompleteGoalModal';
import ItemDoesntExistError from 'Shared/components/ItemDoesntExistError/ItemDoesntExistError';

import { toggleIntercom } from 'Shared/helpers';

import GoalDetailsPageActionItems from './GoalDetailsPageActionItems';
import GoalsDetailsSidebar from './GoalDetailsSidebar/GoalDetailsSidebar';
import GoalInformation from './GoalInformation';
import GoalDetailsRelatedGoals from 'Shared/components/Goals/GoalDetailsRelatedGoals/GoalDetailsRelatedGoals';

import './GoalDetailsPage.scss';

type Props = {
  redirectTo?: string;
  version?: 'full-page' | 'slim';
  handleBackClick: () => void;
};

const GoalDetailsPage = ({ handleBackClick, version = 'full-page' }: Props) => {
  const dispatch = useDispatch();
  const [activeSlug, setActiveSlug] = useState(null);
  const [hasError, toggleHasError] = useToggle(false);
  // This is only used for responsive to force open the sidebar
  const [isSidebarOpen, toggleIsSidebarOpen] = useToggle(false);
  const [isEditing, toggleIsEditing] = useToggle(false);
  const [isCloning, toggleIsCloning] = useToggle(false);
  const [isShowConfetti, toggleIsShowConfetti] = useState(false);
  const [isSavingComplete, toggleIsSavingComplete] = useToggle(false);
  const [completeGoalModalIsOpen, completeGoalModalIsOpenToggle] = useToggle(false);
  const { featureFlagGoalsVersion3: showNewGoals } = useFlags();

  const { slug } = useParams();

  const goal = useSelector(state => GoalsSelectors.getGoalBySlug(state, { slug }))?.toJS() || {};

  const actionItemsLength = useSelector(
    state => getActionItems(state).filter(item => item.goalSlug === goal.slug).length
  );

  useEffect(() => {
    dispatch(goalsActions.fetchSingleGoal(slug))
      .then(response => {
        // check to see if response is valid, if not - user either doesn't have
        // permission to goal, goal slug is invalid, or goal doesn't exist anymore
        if (!response) toggleHasError();
        else {
          setActiveSlug(slug);
        }
      })
      .catch(() => {
        toggleHasError();
      });

    (window as any).analytics.track('Viewed Goal', { goal_slug: goal.slug, goal_name: goal.name });
  }, [slug, setActiveSlug, toggleHasError]);

  useEffect(() => {
    toggleIntercom({ show: false });

    return function cleanup() {
      toggleIntercom({ show: true });
    };
  }, [goal]);

  const handleEditClick = useCallback(() => {
    toggleIsEditing();
  }, [toggleIsEditing]);

  const handleCloneClick = useCallback(() => {
    toggleIsCloning();
  }, [toggleIsCloning]);

  const markComplete = useCallback(
    ({ closeActionItems, resolutionNote }) => {
      toggleIsSavingComplete();
      if (goal.status !== 'missed') {
        toggleIsShowConfetti(true);
      }
      dispatch(goalsActions.markGoalComplete({ closeActionItems, goalSlug: slug, resolutionNote })).then(() => {
        toggleIsSavingComplete();
      });
    },
    [dispatch, slug, toggleIsSavingComplete, goal]
  );

  const handleMarkComplete = useCallback(() => {
    completeGoalModalIsOpenToggle();
  }, [completeGoalModalIsOpenToggle]);

  const handleMarkIncomplete = useCallback(async () => {
    toggleIsSavingComplete();
    toggleIsShowConfetti(false);
    dispatch(goalsActions.markGoalIncomplete(slug)).then(() => {
      toggleIsSavingComplete();
    });
  }, [dispatch, slug, toggleIsSavingComplete]);

  const confettiSettings = {
    max: '200',
    size: '1',
    animate: true,
    props: ['circle', 'square', 'triangle'],
    colors: [
      [165, 104, 246],
      [230, 61, 135],
      [0, 199, 228],
      [253, 214, 126],
    ],
    clock: '75',
    rotate: true,
    start_from_edge: true,
    respawn: false,
  };

  const showActivityPanel = version !== 'slim';
  const showHeader = version !== 'slim';

  const showDetailsPage = activeSlug === slug;
  if (hasError) {
    return (
      <ItemDoesntExistError
        title="We couldn’t find this Goal."
        description="You either no longer have access to it, or it has been deleted."
        buttonCallback={handleBackClick}
      />
    );
  }

  if (isEditing) {
    if (showNewGoals) {
      return (
        <CreateOrUpdateGoalPageV3
          isEdit
          goal={goal}
          toggleDisplay={handleEditClick}
          handleRedirectBack={handleBackClick}
        />
      );
    } else {
      return (
        <CreateOrUpdateGoalPageV2
          isEdit
          goal={goal}
          toggleDisplay={handleEditClick}
          handleRedirectBack={handleBackClick}
        />
      );
    }
  }

  if (isCloning) {
    if (showNewGoals) {
      return (
        <CreateOrUpdateGoalPageV3 goal={goal} toggleDisplay={handleCloneClick} handleRedirectBack={handleBackClick} />
      );
    } else {
      return (
        <CreateOrUpdateGoalPageV2 goal={goal} toggleDisplay={handleCloneClick} handleRedirectBack={handleBackClick} />
      );
    }
  }

  return (
    <>
      <div className="GoalDetailsPage">
        {!showDetailsPage ? (
          <Spinner activeIf />
        ) : (
          <>
            <div className="GoalDetailsPage__content">
              {showHeader && (
                <div className="GoalDetailsPage__header">
                  <WpButton variant="secondary" onClick={handleBackClick} qaTest="btn-goal-detail-page-done">
                    Done
                  </WpButton>
                </div>
              )}
              <div className="GoalDetailsPage__goal">
                <GoalInformation
                  goal={goal}
                  toggleEdit={handleEditClick}
                  toggleClone={handleCloneClick}
                  handleMarkComplete={handleMarkComplete}
                  handleMarkIncomplete={handleMarkIncomplete}
                  isSavingComplete={isSavingComplete}
                  actionItemsLength={actionItemsLength}
                />
              </div>
              {showActivityPanel && (
                <div className="GoalDetailsPage__sidebar-trigger" onClick={toggleIsSidebarOpen}>
                  <span>Comments & History</span>
                  <WpIcon name="chevron-right" color={Colors.gray[800]} />
                </div>
              )}
              <div className="GoalDetailsPage__related-goals">
                <GoalDetailsRelatedGoals goal={goal} />
              </div>
              <div className="GoalDetailsPage__action-items">
                <GoalDetailsPageActionItems
                  isComplete={goal.complete}
                  workspaceSlug={goal.workspaceSlug}
                  goalSlug={goal.slug}
                  goalIsReadOnly={!goal.canEditActionItems}
                />
              </div>
            </div>
            {showActivityPanel && (
              <div className="GoalDetailsPage__comments">
                <GoalsDetailsSidebar goal={goal} isSidebarOpen={isSidebarOpen} toggleSidbar={toggleIsSidebarOpen} />
              </div>
            )}
          </>
        )}
      </div>
      {completeGoalModalIsOpen ? (
        <CompleteGoalModal
          goalSlug={goal.slug}
          goalResolutionNote={goal.resolutionNote}
          onSubmit={markComplete}
          onToggle={completeGoalModalIsOpenToggle}
        />
      ) : null}
      {isShowConfetti && (
        <Confetti confettiSettings={confettiSettings} appendToPage={false} className="GoalDetailsPage__confetti" />
      )}
    </>
  );
};

export default GoalDetailsPage;
